import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Active-selection / summary indicator card: a rounded, softly-tinted panel
 * with a colored leading Material icon, projected content, and an optional
 * trailing action (slot `[u2bIndicatorAction]`, e.g. an edit/remove button).
 *
 * Used for the wizard's "active indicator" cards (selected date, person,
 * relation, …) and every section of the booking summary so they share one
 * padding/border/tint recipe. `accent` only drives the tint + icon color.
 *
 * The accent → icon-color classes below are plain literals so Tailwind keeps
 * them (its content scanner reads the @u2b-ui TypeScript files); the template
 * binds them via NgClass.
 */
export type U2bIndicatorAccent =
    | 'primary'
    | 'teal'
    | 'sky'
    | 'violet'
    | 'slate'
    | 'amber'
    | 'indigo';

const ACCENTS: Record<U2bIndicatorAccent, { color: string; iconClass: string }> = {
    primary: { color: 'var(--p-primary-color)', iconClass: 'text-p-primary' },
    teal:    { color: '#0f766e', iconClass: 'text-teal-700' },
    sky:     { color: '#0284c7', iconClass: 'text-sky-600' },
    violet:  { color: '#7c3aed', iconClass: 'text-violet-600' },
    slate:   { color: '#64748b', iconClass: 'text-slate-500' },
    amber:   { color: '#f59e0b', iconClass: 'text-amber-500' },
    indigo:  { color: '#4f46e5', iconClass: 'text-indigo-600' },
};

@Component({
    selector: 'u2b-indicator-card',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass],
    host: { class: 'block' },
    template: `
        <div class="rounded-lg border border-p-border p-5 flex items-start gap-3"
             [style.background]="tint()">
            @if (icon()) {
                <span class="material-icons flex-shrink-0 mt-0.5" [ngClass]="iconClass()" aria-hidden="true">{{ icon() }}</span>
            }
            <div class="flex-1 min-w-0">
                <ng-content></ng-content>
            </div>
            <ng-content select="[u2bIndicatorAction]"></ng-content>
        </div>
    `,
})
export class U2bIndicatorCardComponent {

    readonly icon = input<string | null>(null);

    readonly accent = input<U2bIndicatorAccent>('primary');

    readonly tint = computed(() => `color-mix(in srgb, ${ACCENTS[this.accent()].color} 8%, transparent)`);

    readonly iconClass = computed(() => ACCENTS[this.accent()].iconClass);
}
